import { z } from 'zod'

export const poolPresetConfigSchema = z.object({
	name: z.string().optional(),
	description: z.string().optional(),
	rewardSize: z.number().optional().nullable(),
	savingSize: z.number().optional().nullable(),
	rewardRatio: z.number().optional().nullable(),
	order: z.number().optional(),
})

export const presetConfigSchema = z.object({
	thresholdType: z.enum(['FIXED', 'MULTIPLIER']).default('FIXED'),
	turnoverThreshold: z
		.number()
		.min(0, 'จำนวนเทิร์นโอเวอร์ต้องมากกว่าหรือเท่ากับ 0')
		.optional()
		.nullable()
		.transform((v) => v ?? 0),
	turnoverMultiplier: z
		.number()
		.min(0, 'จำนวนเท่าต้องมากกว่าหรือเท่ากับ 0')
		.optional()
		.nullable()
		.transform((v) => v ?? 0),
	presetId: z.string().min(1, 'กรุณาเลือก RTP Preset'),
	presetNumber: z
		.number({
			required_error: 'กรุณาระบุเปอร์เซ็น RTP',
			invalid_type_error: 'กรุณาระบุเปอร์เซ็น RTP',
		})
		.min(30, 'เปอร์เซ็น RTP ต้องอยู่ระหว่าง 30-99')
		.max(99, 'เปอร์เซ็น RTP ต้องอยู่ระหว่าง 30-99'),
	order: z.number().optional(),
})

export const walletConditionSchema = z
	.object({
		minimumWithdrawCredit: z.number().optional().nullable(),
		maximumWithdrawCredit: z.number().optional().nullable(),
		turnoverRound: z.number().optional().nullable(),
		gameTypes: z.array(z.string()).optional().nullable(),
		whitelistGameProviders: z.array(z.string()).optional().nullable(),
		usePool: z.boolean().optional().nullable(),
		poolPresetConfigs: z.array(poolPresetConfigSchema).optional().nullable(),
		presetConfigs: z.array(presetConfigSchema).optional().nullable(),
		presetId: z.string().optional().nullable(),
		pgHard: z.boolean().optional().nullable(),
		jokerHard: z.boolean().optional().nullable(),
		jiliHard: z.boolean().optional().nullable(),
		ppHard: z.boolean().optional().nullable(),
		nlcHard: z.boolean().optional().nullable(),
	})
	.superRefine((data, ctx) => {
		const configs = data.presetConfigs
		if (!configs || configs.length === 0) return

		// All configs must share the same thresholdType
		const types = configs.map((c) => c.thresholdType ?? 'FIXED')
		const uniqueTypes = new Set(types)
		if (uniqueTypes.size > 1) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message:
					'Preset ทั้งหมดต้องใช้ประเภทเงื่อนไขเดียวกัน (คงที่ หรือ เท่าของยอดฝาก)',
				path: ['presetConfigs'],
			})
			return
		}

		const fixedConfigs = configs.filter(
			(c) => !c.thresholdType || c.thresholdType === 'FIXED',
		)
		const multiplierConfigs = configs.filter(
			(c) => c.thresholdType === 'MULTIPLIER',
		)

		// No duplicate fixed thresholds
		const fixedValues = fixedConfigs.map((c) => c.turnoverThreshold ?? 0)
		fixedValues.forEach((val, i) => {
			if (fixedValues.indexOf(val) !== i) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'ค่า Threshold ซ้ำกัน กรุณาใช้ค่าที่ไม่ซ้ำกัน',
					path: ['presetConfigs', i, 'turnoverThreshold'],
				})
			}
		})

		// Fixed thresholds must be ordered low to high
		for (let i = 1; i < fixedValues.length; i++) {
			if (fixedValues[i] <= fixedValues[i - 1]) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message:
						'ค่า Threshold ต้องเรียงจากน้อยไปมาก (Preset แรกต้องมีค่าน้อยกว่า Preset ถัดไป)',
					path: ['presetConfigs', i, 'turnoverThreshold'],
				})
			}
		}

		// No duplicate multipliers
		const multiplierValues = multiplierConfigs.map(
			(c) => c.turnoverMultiplier ?? 0,
		)
		multiplierValues.forEach((val, i) => {
			if (multiplierValues.indexOf(val) !== i) {
				const configIndex = configs.indexOf(multiplierConfigs[i])
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'ค่า Multiplier ซ้ำกัน กรุณาใช้ค่าที่ไม่ซ้ำกัน',
					path: ['presetConfigs', configIndex, 'turnoverMultiplier'],
				})
			}
		})

		// Multiplier thresholds must be ordered low to high
		for (let i = 1; i < multiplierValues.length; i++) {
			if (multiplierValues[i] <= multiplierValues[i - 1]) {
				const configIndex = configs.indexOf(multiplierConfigs[i])
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message:
						'ค่า Multiplier ต้องเรียงจากน้อยไปมาก (Preset แรกต้องมีค่าน้อยกว่า Preset ถัดไป)',
					path: ['presetConfigs', configIndex, 'turnoverMultiplier'],
				})
			}
		}

		// Multiplier must be < turnoverRound
		if (data.turnoverRound != null) {
			configs.forEach((config, i) => {
				if (
					config.thresholdType === 'MULTIPLIER' &&
					config.turnoverMultiplier != null &&
					config.turnoverMultiplier >= data.turnoverRound!
				) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: `ค่า Multiplier ต้องน้อยกว่า Turnover Round (${data.turnoverRound}x)`,
						path: ['presetConfigs', i, 'turnoverMultiplier'],
					})
				}
			})
		}
	})

export type WalletConditionSchemaType = z.infer<typeof walletConditionSchema>
export type PoolPresetConfigSchemaType = z.infer<typeof poolPresetConfigSchema>
export type PresetConfigSchemaType = z.infer<typeof presetConfigSchema>
